import type React from "react";
import {ToggleButton, ToggleButtonGroup} from "@mui/material";
import {AccountTreeOutlined, CallSplitOutlined, HelpOutlineOutlined, LinkOutlined, PaidOutlined} from "@mui/icons-material";
import type {SitKpiPill} from "~/sit/components/SitKpiHeader/SitKpiHeader.tsx";
import {SitKpiHeader} from "~/sit/components/SitKpiHeader/SitKpiHeader.tsx";
import type {ContractDetail, LinkStatus} from "../contractExplorerTypes.ts";
import {LINK_STATUSES} from "../contractExplorerTypes.ts";
import {formatMoney} from "../contractExplorerFormat.ts";
import {InContractPurchases} from "./InContractPurchases.tsx";
import {LinksGrid} from "./LinksGrid.tsx";
import {MatchingBasis} from "./MatchingBasis.tsx";
import {Section} from "./Section.tsx";

type Props = {
    bagId: number;
    contract: ContractDetail;
    status: LinkStatus;
    onStatus: (status: LinkStatus) => void;
    onOpenContract: (contractId: number) => void;
};

const STATUS_LABEL: Record<LinkStatus, string> = {linked: 'Linked', competing: 'Competing', candidate: 'Candidate'};
const STATUS_NOTE: Record<LinkStatus, string> = {
    linked: 'Transactions this bundle holds: the highest confidence at or above 0.5.',
    competing: 'Transactions that qualified here too, but another bundle holds them.',
    candidate: 'Transactions linkage linked below 0.5; none of them counts as spend under contract.',
};

/** What linkage linked to the bundle: how it read the spend, the totals, the transactions, and spend per line. */
export const SpendTab: React.FC<Props> = ({bagId, contract, status, onStatus, onOpenContract}) => {
    const counts = contract.link_counts;
    const whole = contract.purchases.find(p => p.line === '');
    const kpis: SitKpiPill[] = [
        {icon: <LinkOutlined/>, tone: 'ok', label: 'Linked', value: counts.linked},
        {icon: <CallSplitOutlined/>, tone: 'warn', label: 'Competing', value: counts.competing,
            help: STATUS_NOTE.competing},
        {icon: <HelpOutlineOutlined/>, tone: 'plain', label: 'Candidates', value: counts.candidate,
            help: STATUS_NOTE.candidate},
        {icon: <AccountTreeOutlined/>, tone: 'info', label: 'Linked to the bundle as a whole', value: whole?.links ?? 0,
            help: 'Linked transactions linkage could not place on one contract line.'},
        {icon: <PaidOutlined/>, tone: 'ok', label: 'Spend under contract',
            value: formatMoney(contract.linked_spend, contract.spend_currency)},
    ];
    return <>
        <MatchingBasis contract={contract}/>
        <SitKpiHeader items={kpis}/>
        <Section title="Transactions" sub={STATUS_NOTE[status]}
                 right={(
                     <ToggleButtonGroup size="small" exclusive value={status} className="ci-segmented"
                                        onChange={(_e, next: LinkStatus | null) => next && onStatus(next)}>
                         {LINK_STATUSES.map(s => (
                             <ToggleButton key={s} value={s}>{STATUS_LABEL[s]} · {counts[s]}</ToggleButton>
                         ))}
                     </ToggleButtonGroup>
                 )}>
            <LinksGrid key={status} bagId={bagId} contract={contract} status={status} onOpenContract={onOpenContract}/>
        </Section>
        <InContractPurchases contract={contract}/>
    </>;
};
